import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, IconButton,
  Button, MenuItem, Select, FormControl, InputLabel, CircularProgress, Alert, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogContentText, DialogActions, Snackbar, TableSortLabel, TextField
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/ru';
import * as XLSX from 'xlsx-js-style';
import { Refresh, Delete, DeleteSweep, CalendarMonth, Download } from '@mui/icons-material';
import axios from 'axios';
import { extractArrayFromResponse } from '../utils/apiHelpers';
import { ColumnToggle } from './common/ColumnToggle';

dayjs.locale('ru');

interface Record {
  id: number; date: string; guide_name: string; tour_name: string; program_name?: string; time: string;
  is_english: boolean; period_id: number; people_count?: number; customer?: string;
  class_name?: string; tour_id?: number; tour_duration?: string;
}
interface Period { id: number; month_name: string; year: number; is_active: number; }
interface Tour { id: number; name: string; duration: string; }
interface Guide { id: number; name: string; short_name?: string; }

type Order = 'asc' | 'desc';
type SortableKey = 'id' | 'date' | 'guide_name' | 'tour_name' | 'time' | 'is_english' | 'customer' | 'class_name';

const parseDurationToMinutes = (durationStr: string | null | undefined): number => {
  if (!durationStr) return 70;
  const str = durationStr.trim();
  const colonMatch = str.match(/^(\d+):(\d{2})$/);
  if (colonMatch) return parseInt(colonMatch[1]) * 60 + parseInt(colonMatch[2]);
  const decimalMatch = str.match(/^(\d+\.?\d*)$/);
  if (decimalMatch) return Math.round(parseFloat(decimalMatch[1]) * 60);
  return 70;
};

const calculateBZZ = (startTime: string, durationMin: number): string => {
  if (!startTime || !startTime.includes(':')) return '—';
  const [h, m] = startTime.split(':').map(Number);
  const totalMin = h * 60 + m + durationMin;
  return `${String(Math.floor(totalMin / 60)).padStart(2, '0')}:${String(totalMin % 60).padStart(2, '0')}`;
};

const CalendarPage: React.FC = () => {
  const [records, setRecords] = useState<Record[]>([]);
  const [periods, setPeriods] = useState<Period[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<number>(0);
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterClass, setFilterClass] = useState('');
  const [filterEnglish, setFilterEnglish] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<SortableKey>('id');

  const columns = [
    { key: 'id', label: 'ID', defaultVisible: true }, 
    { key: 'date', label: 'Дата', defaultVisible: true },
    { key: 'guide_name', label: 'Экскурсовод', defaultVisible: true }, 
    { key: 'tour_name', label: 'Экскурсия', defaultVisible: true },
    { key: 'program_name', label: 'Программа экскурсии', defaultVisible: true },
    { key: 'time', label: 'Время', defaultVisible: true }, 
    { key: 'customer', label: 'Заказчик', defaultVisible: true },
    { key: 'class_name', label: 'Класс', defaultVisible: true },
    { key: 'people_count', label: 'Кол-во', defaultVisible: true },
    { key: 'is_english', label: 'Англ', defaultVisible: true },
  ];
  const [visibleColumns, setVisibleColumns] = useState<string[]>([]);

  useEffect(() => {
    const saved = localStorage.getItem('calendar_visible_columns');
    if (saved) { try { setVisibleColumns(JSON.parse(saved)); return; } catch (e) {} }
    setVisibleColumns(columns.filter(c => c.defaultVisible).map(c => c.key));
  }, []);

  const loadData = async (periodId?: number) => {
    try {
      setLoading(true); setError(null);
      const periodsRes = await axios.get('http://192.168.50.43:5002/api/periods');
      const periodsData = extractArrayFromResponse(periodsRes.data);
      setPeriods(periodsData);
      let targetPeriodId = periodId;
      if (!targetPeriodId) {
        const activePeriod = periodsData.find((p: Period) => p.is_active === 1);
        targetPeriodId = activePeriod?.id || periodsData[0]?.id || 0;
        setSelectedPeriod(targetPeriodId);
      }
      if (targetPeriodId) {
        const recordsRes = await axios.get(`http://192.168.50.43:5002/api/calendar/period/${targetPeriodId}`);
        setRecords(extractArrayFromResponse(recordsRes.data));
      } else { setRecords([]); }
    } catch (err) { console.error('Ошибка загрузки:', err); setError('Не удалось загрузить данные'); setRecords([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, []);
  const handlePeriodChange = (periodId: number) => { setSelectedPeriod(periodId); loadData(periodId); };
  const filteredRecords = records.filter(r => {
    if (selectedDate && r.date !== selectedDate.format('DD.MM.YYYY')) return false;
    if (filterCustomer && !r.customer?.toLowerCase().includes(filterCustomer.toLowerCase())) return false;
    if (filterClass && !r.class_name?.toLowerCase().includes(filterClass.toLowerCase())) return false;
    if (filterEnglish === 'yes' && !r.is_english) return false;
    if (filterEnglish === 'no' && r.is_english) return false;
    return true;
  });

  const handleExportToExcel = async () => {
    try {
      setLoading(true);
      const dateParam = selectedDate ? selectedDate.format('YYYY-MM-DD') : '';
      const url = dateParam ? `http://192.168.50.43:5002/api/calendar/export?date=${dateParam}` : 'http://192.168.50.43:5002/api/calendar/export';
      const [calRes, toursRes] = await Promise.all([
        axios.get(url), axios.get('http://192.168.50.43:5002/api/tours')
      ]);
      const data = extractArrayFromResponse(calRes.data);
      const tours = extractArrayFromResponse(toursRes.data) as Tour[];

      if (data.length === 0) { setSnackbar({ open: true, message: 'Нет данных для экспорта', severity: 'warning' }); return; }

      const dateStr = selectedDate ? selectedDate.format('DD MMM YYYY') : '';
      const headerText = selectedDate ? `Календарь на ${dateStr} для Администратора` : `Календарь для Администратора`;
      const wb = XLSX.utils.book_new();

      // Шапка по эталону
      const headers = [
        '№', 'Дата', 'День недели', 'начало экскурсии', 'БЗЗ нач. сеанса', 
        'Заказчик (ЧЛ, организация, ШКОЛА)', 'Класс', 'Чел-к', 
        'Наименование экскурсии', 'Программа экскурсии', 'Менеджер', 
        'Экскурсовод', 'Английский', 'Подпись экскурсовода', 'Подпись администратора'
      ];

      const tableData = data.map((r: any, idx: number) => {
        const durationMin = parseDurationToMinutes(r.tour_duration);
        const bzzTime = calculateBZZ(r.time, durationMin);
        const customerInfo = r.customer ? `${r.customer}` : '';
        
        let dayOfWeekStr = '';
        if (r.date) {
          const parts = r.date.split('.');
          if (parts.length === 3) {
            const d = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
            const days = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'];
            dayOfWeekStr = days[d.getDay()];
          }
        }

        const isEnglish = r.is_english ? 'Да' : '';
        // programInfo теперь берем из базы (r.program_name), а не собираем вручную
        const programInfo = r.program_name || '';
        
        return [
          idx + 1, 
          r.date, 
          dayOfWeekStr, 
          r.time, 
          bzzTime, 
          customerInfo, 
          r.class_name || '', 
          r.people_count || 0, 
          r.tour_name, 
          programInfo, 
          'ВЕБСАЙТ', 
          r.guide_name, 
          isEnglish, 
          '', 
          ''
        ];
      });

      tableData.push([`Количество экскурсий: ${data.length}`, '', '', '', '', '', '', '', '', '', '', '', '', '', '']);

      const wsData: any[][] = [
        [headerText, '', '', '', '', '', '', '', '', '', '', '', '', '', ''], 
        ['', '', '', '', '', '', '', '', '', '', '', '', '', '', ''], 
        headers, 
        ...tableData
      ];

      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws['!cols'] = [
        { wch: 5 }, { wch: 12 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, 
        { wch: 40 }, { wch: 10 }, { wch: 8 }, { wch: 30 }, { wch: 30 }, 
        { wch: 12 }, { wch: 22 }, { wch: 10 }, { wch: 18 }, { wch: 18 }
      ];
      
      const lastRow = wsData.length - 1;
      ws['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 14 } }, 
        { s: { r: lastRow, c: 0 }, e: { r: lastRow, c: 4 } }
      ];

      const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
      const thinBorder = { top: { style: 'thin' }, bottom: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' } };

      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          if (!ws[cellAddress]) continue;
          
          if (R === 0) {
            ws[cellAddress].s = { font: { bold: true, sz: 14, name: 'Calibri' }, alignment: { horizontal: 'center', vertical: 'center' } };
          } else if (R === 2) {
            ws[cellAddress].s = { font: { bold: true, sz: 10, name: 'Calibri' }, alignment: { horizontal: 'center', vertical: 'center', wrapText: true }, border: thinBorder, fill: { fgColor: { rgb: 'D9E1F2' } } };
          } else if (R > 2 && R < lastRow) {
            let align: any = { vertical: 'center', wrapText: true };
            if ([1, 2, 3, 4, 6, 7, 10].includes(C)) {
              align.horizontal = 'center';
            } else {
              align.indent = 2;
            }
            ws[cellAddress].s = { font: { sz: 10, name: 'Calibri' }, alignment: align, border: thinBorder };
          } else if (R === lastRow) {
            ws[cellAddress].s = { font: { bold: true, sz: 11, name: 'Calibri' }, alignment: { horizontal: 'center', vertical: 'center' } };
          }
        }
      }

      XLSX.utils.book_append_sheet(wb, ws, 'Календарь');
      const fileName = `calendar${selectedDate ? '_' + selectedDate.format('DD.MM.YYYY') : ''}.xlsx`;
      XLSX.writeFile(wb, fileName);
      setSnackbar({ open: true, message: `Экспортировано ${data.length} записей`, severity: 'success' });
    } catch (err) { 
      console.error('Ошибка экспорта:', err); 
      setSnackbar({ open: true, message: 'Ошибка при экспорте', severity: 'error' }); 
    } finally { 
      setLoading(false); 
    }
  };

  const handleDeleteRecord = async (id: number) => {
    try { await axios.delete(`http://192.168.50.43:5002/api/calendar/${id}`); setSnackbar({ open: true, message: 'Запись удалена', severity: 'success' }); loadData(selectedPeriod); }
    catch (err) { setSnackbar({ open: true, message: 'Ошибка удаления записи', severity: 'error' }); }
    setDeleteDialogOpen(false);
  };

  const handleClearPeriod = async () => {
    if (!selectedPeriod || !window.confirm('Удалить все записи за выбранный период?')) return;
    try { await axios.delete(`http://192.168.50.43:5002/api/calendar/period/${selectedPeriod}`); setSnackbar({ open: true, message: 'Период очищен', severity: 'success' }); loadData(selectedPeriod); }
    catch (err) { setSnackbar({ open: true, message: 'Ошибка очистки периода', severity: 'error' }); }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Удалить ВСЕ записи календаря?')) return;
    try { await axios.delete('http://192.168.50.43:5002/api/calendar'); setSnackbar({ open: true, message: 'Календарь очищен', severity: 'success' }); loadData(selectedPeriod); }
    catch (err) { setSnackbar({ open: true, message: 'Ошибка очистки календаря', severity: 'error' }); }
  };

  const handleRequestSort = (property: SortableKey) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedRecords = [...filteredRecords].sort((a, b) => {
    let aValue: any = a[orderBy]; let bValue: any = b[orderBy];
    if (orderBy === 'is_english') { aValue = a.is_english ? 1 : 0; bValue = b.is_english ? 1 : 0; }
    if (orderBy === 'date') { aValue = new Date(a.date.split('.').reverse().join('-')).getTime(); bValue = new Date(b.date.split('.').reverse().join('-')).getTime(); }
    if (typeof aValue === 'string') { aValue = aValue.toLowerCase(); bValue = bValue.toLowerCase(); }
    if (aValue < bValue) return order === 'asc' ? -1 : 1;
    if (aValue > bValue) return order === 'asc' ? 1 : -1;
    return 0;
  });

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}><CircularProgress /></Box>;
  if (error) return <Box sx={{ p: 3 }}><Alert severity="error">{error}</Alert></Box>;
  const isVisible = (key: string) => visibleColumns.includes(key);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="outlined" startIcon={<Refresh />} onClick={() => loadData(selectedPeriod)} size="small">Обновить</Button>
            <Button variant="contained" color="success" startIcon={<Download />} onClick={handleExportToExcel} size="small">Excel</Button>
            <Button variant="outlined" color="warning" startIcon={<DeleteSweep />} onClick={handleClearPeriod} size="small">Очистить период</Button>
            <Button variant="outlined" color="error" startIcon={<DeleteSweep />} onClick={handleClearAll} size="small">Очистить всё</Button>
          </Box>
        </Box>

        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select 
                value={selectedPeriod} 
                onChange={(e) => handlePeriodChange(Number(e.target.value))} 
                displayEmpty
                sx={{ height: 40, borderRadius: 2 }}
              >
                <MenuItem value={0} disabled><em>Период</em></MenuItem>
                {periods.map((p) => (<MenuItem key={p.id} value={p.id}>{p.month_name} {p.year}</MenuItem>))}
              </Select>
            </FormControl>
            
            <DatePicker 
              value={selectedDate} 
              onChange={(newValue) => setSelectedDate(newValue)} 
              slotProps={{ 
                textField: { 
                  size: 'small', 
                  placeholder: "Дата",
                  sx: { width: 160, '& .MuiInputBase-root': { height: 40, borderRadius: 2 } } 
                } 
              }} 
            />
            
            <TextField
              size="small"
              placeholder="Заказчик"
              value={filterCustomer}
              onChange={(e) => setFilterCustomer(e.target.value)}
              sx={{ width: 180, '& .MuiOutlinedInput-root': { height: 40, borderRadius: 2 } }}
            />
            
            <TextField
              size="small"
              placeholder="Класс"
              value={filterClass}
              onChange={(e) => setFilterClass(e.target.value)}
              sx={{ width: 120, '& .MuiOutlinedInput-root': { height: 40, borderRadius: 2 } }}
            />
            
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <Select 
                value={filterEnglish} 
                onChange={(e) => setFilterEnglish(e.target.value)} 
                displayEmpty
                sx={{ height: 40, borderRadius: 2 }}
              >
                <MenuItem value=""><em>Английский</em></MenuItem>
                <MenuItem value="yes">Да</MenuItem>
                <MenuItem value="no">Нет</MenuItem>
              </Select>
            </FormControl>

            {(selectedDate || filterCustomer || filterClass || filterEnglish) && (
              <Button 
                variant="outlined" 
                onClick={() => { setSelectedDate(null); setFilterCustomer(''); setFilterClass(''); setFilterEnglish(''); }} 
                sx={{ height: 40, textTransform: 'none', borderRadius: 2 }}
              >
                Сбросить
              </Button>
            )}
          </Box>
        </Paper>

        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <ColumnToggle columns={columns} storageKey="calendar_visible_columns" onChange={(cols) => setVisibleColumns(cols)} />
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  {isVisible('id') && <TableCell><TableSortLabel active={orderBy === 'id'} direction={orderBy === 'id' ? order : 'asc'} onClick={() => handleRequestSort('id')} sx={{ fontWeight: 'bold' }}>ID</TableSortLabel></TableCell>}
                  {isVisible('date') && <TableCell><TableSortLabel active={orderBy === 'date'} direction={orderBy === 'date' ? order : 'asc'} onClick={() => handleRequestSort('date')} sx={{ fontWeight: 'bold' }}>Дата</TableSortLabel></TableCell>}
                  <TableCell sx={{ fontWeight: 'bold' }}>День недели</TableCell>
                  {isVisible('time') && <TableCell><TableSortLabel active={orderBy === 'time'} direction={orderBy === 'time' ? order : 'asc'} onClick={() => handleRequestSort('time')} sx={{ fontWeight: 'bold' }}>Время</TableSortLabel></TableCell>}
                  <TableCell sx={{ fontWeight: 'bold' }}>БЗЗ</TableCell>
                  {isVisible('customer') && <TableCell><TableSortLabel active={orderBy === 'customer'} direction={orderBy === 'customer' ? order : 'asc'} onClick={() => handleRequestSort('customer')} sx={{ fontWeight: 'bold' }}>Заказчик</TableSortLabel></TableCell>}
                  {isVisible('class_name') && <TableCell><TableSortLabel active={orderBy === 'class_name'} direction={orderBy === 'class_name' ? order : 'asc'} onClick={() => handleRequestSort('class_name')} sx={{ fontWeight: 'bold' }}>Класс</TableSortLabel></TableCell>}
                  {isVisible('people_count') && <TableCell sx={{ fontWeight: 'bold' }}>Чел.</TableCell>}
                  {isVisible('tour_name') && <TableCell><TableSortLabel active={orderBy === 'tour_name'} direction={orderBy === 'tour_name' ? order : 'asc'} onClick={() => handleRequestSort('tour_name')} sx={{ fontWeight: 'bold' }}>Экскурсия</TableSortLabel></TableCell>}
                  {isVisible('program_name') && <TableCell sx={{ fontWeight: 'bold' }}>Программа экскурсии</TableCell>}
                  {isVisible('guide_name') && <TableCell><TableSortLabel active={orderBy === 'guide_name'} direction={orderBy === 'guide_name' ? order : 'asc'} onClick={() => handleRequestSort('guide_name')} sx={{ fontWeight: 'bold' }}>Экскурсовод</TableSortLabel></TableCell>}
                  {isVisible('is_english') && <TableCell><TableSortLabel active={orderBy === 'is_english'} direction={orderBy === 'is_english' ? order : 'asc'} onClick={() => handleRequestSort('is_english')} sx={{ fontWeight: 'bold' }}>Англ</TableSortLabel></TableCell>}
                  <TableCell align="right">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRecords.length === 0 ? (
                  <TableRow><TableCell colSpan={13} align="center"><Typography color="text.secondary" sx={{ py: 2 }}>Нет записей{selectedDate ? ' на выбранную дату' : ''}</Typography></TableCell></TableRow>
                ) : (
                  sortedRecords.map((record) => {
                    const durationMin = parseDurationToMinutes(record.tour_duration);
                    const bzzTime = calculateBZZ(record.time, durationMin);
                    
                    let dayOfWeekStr = '';
                    if (record.date) {
                      const parts = record.date.split('.');
                      if (parts.length === 3) {
                        const d = new Date(Number(parts[2]), Number(parts[1]) - 1, Number(parts[0]));
                        const days = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
                        dayOfWeekStr = days[d.getDay()];
                      }
                    }

                    return (
                      <TableRow key={record.id} hover>
                        {isVisible('id') && <TableCell>{record.id}</TableCell>}
                        {isVisible('date') && <TableCell>{record.date}</TableCell>}
                        <TableCell>{dayOfWeekStr}</TableCell>
                        {isVisible('time') && <TableCell>{record.time || '-'}</TableCell>}
                        <TableCell>{bzzTime}</TableCell>
                        {isVisible('customer') && (
                          <TableCell>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 200 }} title={record.customer || ''}>
                              {record.customer || '—'}
                            </Typography>
                          </TableCell>
                        )}
                        {isVisible('class_name') && <TableCell>{record.class_name || '—'}</TableCell>}
                      {isVisible('people_count') && <TableCell align="center">{record.people_count || 0}</TableCell>}
                        {isVisible('tour_name') && <TableCell>{record.tour_name}</TableCell>}
                        {isVisible('program_name') && <TableCell sx={{ maxWidth: 200, fontSize: '0.8rem', color: 'text.secondary' }}>{record.program_name || '—'}</TableCell>}
                        {isVisible('guide_name') && <TableCell>{record.guide_name}</TableCell>}
                        {isVisible('is_english') && <TableCell><Chip label={record.is_english ? 'Да' : 'Нет'} size="small" color={record.is_english ? 'primary' : 'default'} /></TableCell>}
                        <TableCell align="right">
                          <Tooltip title="Удалить запись">
                            <IconButton size="small" color="error" onClick={() => { setDeleteTarget({ id: record.id, name: `${record.date} - ${record.guide_name}` }); setDeleteDialogOpen(true); }}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Удалить запись?</DialogTitle>
          <DialogContent><DialogContentText>Вы уверены что хотите удалить запись: <strong>{deleteTarget?.name}</strong>?</DialogContentText></DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Отмена</Button>
            <Button onClick={() => deleteTarget && handleDeleteRecord(deleteTarget.id)} color="error" variant="contained">Удалить</Button>
          </DialogActions>
        </Dialog>

        <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default CalendarPage;
